import {
  ActionMenu,
  AppFrame,
  Button,
  CommandPalette,
  useAccent,
  WriteLockProvider,
  type Command,
  type MenuGroup,
} from '@kanos/ui';
import {
  AccountMenu,
  AppFooter,
  MaintenanceBanner,
  HelpMenu,
  PlaceLinks,
  profileCommands,
  SectionNav,
  type NavLinkRenderer,
} from '@kanos/web-core';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { BookOpen, Building2, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useClientSearch } from './client-search';
import { articleFor, HELP_ARTICLES } from './help/articles';
import { AnnouncementsDialog, useUnreadAnnouncements } from './help/announcements';
import { ContractBanner, ContractGate, useContractWriteLock } from './firm/contract-banner';
import { useMe } from './me';
import { AreaBases } from './area-link';
import { AREA_PLACES, areasFor, baseOf, MAIN_NAVIGATION, sectionsFor } from './navigation';
import { FirmPolling, TaskBell } from './task-bell';
import { useSwitchFirm } from './session';

/** The router's link for the shared navigation; paths come from the navigation itself. */
const routerLink: NavLinkRenderer = ({ to, children, ...props }) => (
  <Link to={to} {...props}>
    {children}
  </Link>
);

/**
 * The firms to switch between (after Corly's tenant switcher) - only where there is something to
 * choose: whoever belongs to one firm finds its name under the word mark and in the account menu.
 */
function useFirmChoice(): { group: MenuGroup | null; isPending: boolean } {
  const me = useMe();
  const switchFirm = useSwitchFirm();
  if (me.firms.length < 2) return { group: null, isPending: false };
  return {
    isPending: switchFirm.isPending,
    group: {
      id: 'firms',
      title: 'Kanzlei wechseln',
      selected: me.activeTenantId,
      onSelect: (tenantId) => {
        if (tenantId !== me.activeTenantId) switchFirm.mutate(tenantId);
      },
      items: me.firms.map((firm) => ({ id: firm.tenantId, label: firm.name })),
    },
  };
}

/**
 * Switching the firm as a pill in the bar - from `lg` only: on a tablet the bar holds the places
 * and the account, and the account menu offers the switch instead.
 */
function FirmSwitcher({ group, isPending }: { group: MenuGroup; isPending: boolean }) {
  const me = useMe();
  const active = me.firms.find((f) => f.tenantId === me.activeTenantId);
  return (
    <div className="max-lg:hidden">
      <ActionMenu
        label="Kanzlei wechseln"
        trigger={
          <Button variant="secondary" size="sm" isPending={isPending}>
            <Building2 aria-hidden className="size-4" />
            <span className="max-w-48 truncate">{active?.name ?? 'Kanzlei wählen'}</span>
            <ChevronDown aria-hidden className="size-4" />
          </Button>
        }
        groups={[group]}
      />
    </div>
  );
}

/**
 * Everything the command palette leads to - the same places as the navigation, filtered by the
 * same rights; a second list would drift apart.
 */
function useCommands(): Command[] {
  const me = useMe();
  const navigate = useNavigate();
  const go = (to: string) => () => void navigate({ to });
  return [
    ...MAIN_NAVIGATION.map(({ to, label, icon }) => ({
      id: to,
      label,
      group: 'Bereiche',
      icon,
      onAction: go(to),
    })),
    ...AREA_PLACES.flatMap(({ base, title }) =>
      areasFor(base, me.firmAdmin).flatMap((group) =>
        group.areas.map((area) => ({
          id: `${base}/${area.slug}`,
          label: area.title,
          group: title,
          keywords: `${group.title} ${area.description}`,
          icon: area.icon,
          onAction: go(`${base}/${area.slug}`),
        })),
      ),
    ),
    ...profileCommands(go),
    ...HELP_ARTICLES.map((article) => ({
      id: `/help/${article.slug}`,
      label: article.title,
      group: 'Hilfe',
      keywords: `Hilfe ${article.summary}`,
      icon: BookOpen,
      onAction: () => void navigate({ to: '/help/$slug', params: { slug: article.slug } }),
    })),
  ];
}

/** Help on the page one is on, all articles, the tour, what is new and the shortcuts. */
function Help({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const { activeTenantId } = useMe();
  const unread = useUnreadAnnouncements(activeTenantId);
  const [newsOpen, setNewsOpen] = useState(false);
  const article = articleFor(pathname);
  return (
    <>
      <HelpMenu
        page={
          article && {
            title: article.title,
            open: () => void navigate({ to: '/help/$slug', params: { slug: article.slug } }),
          }
        }
        onArticles={() => void navigate({ to: '/help' })}
        onTour={() => void navigate({ to: '/', search: { tour: 1 } })}
        news={{ unread, open: () => setNewsOpen(true) }}
      />
      <AnnouncementsDialog
        tenantId={activeTenantId}
        isOpen={newsOpen}
        onClose={() => setNewsOpen(false)}
      />
    </>
  );
}

/** The word mark - the way home - and under it the firm one works in. */
function Brand({ firm }: { firm: string | undefined }) {
  return (
    <Link
      to="/"
      aria-label="Zur Startseite"
      className="grid min-w-0 rounded-sm leading-tight outline-none focus-visible:outline-2 focus-visible:outline-focus"
    >
      <span className="text-xl font-bold tracking-[-0.03em]">kanos</span>
      {firm && <span className="truncate text-sm text-muted">{firm}</span>}
    </Link>
  );
}

/**
 * The frame of the firm app (design direction J): the bar with the word mark, the main places
 * and the account; the areas of a section (settings, profile) beside its pages; the page is the
 * only thing that scrolls.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // The firm's colour on every surface of the firm app (design direction J).
  useAccent(me.accentColor);
  const activeFirm = me.firms.find((f) => f.tenantId === me.activeTenantId);
  const section = sectionsFor(me.firmAdmin).find((s) => pathname.startsWith(`${s.base}/`));
  const commands = useCommands();
  const clientSearch = useClientSearch();
  const firmChoice = useFirmChoice();
  const writeLock = useContractWriteLock();
  const account = (
    <AccountMenu
      me={me}
      context={activeFirm?.name}
      onProfile={() => void navigate({ to: '/profile' })}
      // One entry with the current firm, the firms behind it: on a phone and a tablet the switch.
      extra={
        firmChoice.group
          ? [{ ...firmChoice.group, folded: { label: 'Kanzlei wechseln', icon: Building2 } }]
          : []
      }
    />
  );

  return (
    <AppFrame
      brand={<Brand firm={activeFirm?.name} />}
      places={(variant) => (
        <PlaceLinks
          link={routerLink}
          places={MAIN_NAVIGATION}
          pathname={pathname}
          variant={variant}
          go={(to) => void navigate({ to })}
        />
      )}
      section={section && <SectionNav link={routerLink} section={section} pathname={pathname} />}
      actions={
        <>
          {firmChoice.group && (
            <FirmSwitcher group={firmChoice.group} isPending={firmChoice.isPending} />
          )}
          <CommandPalette commands={commands} {...clientSearch} />
          <Help pathname={pathname} />
          <TaskBell />
          {account}
        </>
      }
      mobileActions={
        <>
          <CommandPalette commands={commands} {...clientSearch} />
          <TaskBell />
          {account}
        </>
      }
      footer={<AppFooter />}
      notice={
        <>
          <MaintenanceBanner />
          <ContractBanner />
        </>
      }
    >
      <AreaBases value={baseOf}>
        <FirmPolling />
        <ContractGate>
          <WriteLockProvider reason={writeLock}>{children}</WriteLockProvider>
        </ContractGate>
      </AreaBases>
    </AppFrame>
  );
}
